import { logger } from './logger';

/**
 * Utilitaires centralisés pour la gestion des dates
 *
 * Ce fichier élimine les doublons et standardise les conversions de dates
 * à travers toute l'application.
 *
 * IMPORTANT - Assumptions de timezone:
 * - Les dates sont stockées en BD comme DATE (YYYY-MM-DD) sans timezone
 * - Les dates sont traitées comme "date-only" (pas d'heure)
 * - Les conversions utilisent UTC midnight pour éviter les décalages de jour
 * - L'utilisateur saisit sa date locale, envoyée comme UTC midnight à l'API
 */

/**
 * Convertir une date pour un input HTML type="date"
 *
 * @param date - Date au format ISO, string "YYYY-MM-DD", ou objet Date
 * @returns String au format "YYYY-MM-DD" (compatible input HTML)
 *
 * @example
 * toInputFormat("2025-10-21T10:30:00Z") // "2025-10-21"
 * toInputFormat(new Date("2025-10-21")) // "2025-10-21"
 * toInputFormat("") // ""
 * toInputFormat(null) // ""
 */
export const toInputFormat = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    logger.error('Error formatting date for input:', error);
    return '';
  }
};

/**
 * Convertir une date pour envoi à l'API (format ISO-8601)
 *
 * IMPORTANT: Assume UTC midnight pour les dates-only.
 * Cela évite les problèmes de timezone (ex: saisie "2025-10-21" reste "2025-10-21").
 *
 * @param dateStr - Date au format "YYYY-MM-DD" ou ISO complet
 * @returns String au format ISO "YYYY-MM-DDTHH:mm:ss.SSSZ"
 *
 * @example
 * toISO("2025-10-21") // "2025-10-21T00:00:00.000Z"
 * toISO("2025-10-21T10:30:00Z") // "2025-10-21T10:30:00Z" (inchangé)
 * toISO("") // Date actuelle en ISO
 */
export const toISO = (dateStr: string): string => {
  if (!dateStr) return new Date().toISOString();

  try {
    // Si déjà au format ISO complet, retourner tel quel
    if (dateStr.includes('T')) return dateStr;

    // Sinon, ajouter T00:00:00.000Z pour UTC midnight
    return new Date(dateStr + 'T00:00:00.000Z').toISOString();
  } catch (error) {
    logger.error('Error converting date to ISO:', error);
    return new Date().toISOString();
  }
};

/**
 * Formatter une date pour affichage localisé court
 *
 * @param date - Date sous n'importe quel format
 * @param locale - Locale à utiliser (défaut: 'fr-FR')
 * @returns String au format court selon la locale
 *
 * @example
 * formatDate("2025-10-21", "fr-FR") // "21/10/2025"
 * formatDate("2025-10-21", "en-US") // "10/21/2025"
 * formatDate("2025-10-21", "en-GB") // "21/10/2025"
 * formatDate("2025-10-21", "de-DE") // "21.10.2025"
 * formatDate("2025-10-21", "es-ES") // "21/10/2025"
 * formatDate(new Date("2025-10-21")) // "21/10/2025" (défaut fr-FR)
 */
export const formatDate = (
  date: Date | string | number | null | undefined,
  locale: string = 'fr-FR'
): string => {
  if (!date) return '';

  try {
    const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(d);
  } catch (error) {
    logger.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formatter une date pour affichage localisé long
 *
 * @param date - Date sous n'importe quel format
 * @param locale - Locale à utiliser (défaut: 'fr-FR')
 * @returns String au format long selon la locale
 *
 * @example
 * formatDateLong("2025-10-21", "fr-FR") // "21 octobre 2025"
 * formatDateLong("2025-10-21", "en-US") // "October 21, 2025"
 * formatDateLong("2025-10-21", "en-GB") // "21 October 2025"
 * formatDateLong("2025-10-21", "de-DE") // "21. Oktober 2025"
 * formatDateLong("2025-10-21", "es-ES") // "21 de octubre de 2025"
 * formatDateLong("2025-10-21", "it-IT") // "21 ottobre 2025"
 */
export const formatDateLong = (
  date: Date | string | number | null | undefined,
  locale: string = 'fr-FR'
): string => {
  if (!date) return '';

  try {
    const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';

    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(d);
  } catch (error) {
    logger.error('Error formatting date long:', error);
    return '';
  }
};

/**
 * Formatter une date pour affichage français court
 *
 * @deprecated Utilisez formatDate(date, 'fr-FR') pour plus de flexibilité
 * @param date - Date sous n'importe quel format
 * @returns String au format "DD/MM/YYYY"
 *
 * @example
 * toFrench("2025-10-21") // "21/10/2025"
 * toFrench(new Date("2025-10-21")) // "21/10/2025"
 * toFrench(1729468800000) // "21/10/2024" (epoch ms)
 */
export const toFrench = (date: Date | string | number | null | undefined): string => {
  return formatDate(date, 'fr-FR');
};

/**
 * Formatter une date pour affichage français long
 *
 * @deprecated Utilisez formatDateLong(date, 'fr-FR') pour plus de flexibilité
 * @param date - Date sous n'importe quel format
 * @returns String au format "D mois YYYY" (ex: "21 octobre 2025")
 *
 * @example
 * toFrenchLong("2025-10-21") // "21 octobre 2025"
 * toFrenchLong(new Date()) // "22 octobre 2025"
 */
export const toFrenchLong = (date: Date | string | number | null | undefined): string => {
  return formatDateLong(date, 'fr-FR');
};

/**
 * Formatter une date pour Factur-X (norme EN 16931)
 *
 * @param date - Date sous n'importe quel format
 * @returns String au format "YYYYMMDD"
 *
 * @example
 * toFacturX("2025-10-21") // "20251021"
 * toFacturX(new Date("2025-10-21")) // "20251021"
 */
export const toFacturX = (date: Date | string | null | undefined): string => {
  if (!date) return '';

  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}${month}${day}`;
  } catch (error) {
    logger.error('Error formatting date for Factur-X:', error);
    return '';
  }
};

/**
 * Vérifier si une date est valide
 *
 * @param date - Date à vérifier
 * @returns true si la date est valide, false sinon
 *
 * @example
 * isValidDate("2025-10-21") // true
 * isValidDate("invalid") // false
 * isValidDate(null) // false
 */
export const isValidDate = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return !isNaN(d.getTime());
  } catch {
    return false;
  }
};

/**
 * Vérifier si une date est valide ET possible (pas de normalisation)
 *
 * Cette fonction rejette les dates impossibles comme le 30 février,
 * qui sont normalisées par JavaScript (ex: 2025-02-30 devient 2025-03-02).
 *
 * @param date - Date à vérifier (format YYYY-MM-DD ou Date object)
 * @returns true si la date est valide ET correspond exactement à l'input
 *
 * @example
 * isStrictValidDate("2025-10-21") // true
 * isStrictValidDate("2025-02-30") // false (30 février n'existe pas)
 * isStrictValidDate("2025-02-29") // false (2025 n'est pas bissextile)
 * isStrictValidDate("2024-02-29") // true (2024 est bissextile)
 * isStrictValidDate("2025-04-31") // false (avril a 30 jours)
 * isStrictValidDate("invalid") // false
 * isStrictValidDate(null) // false
 */
export const isStrictValidDate = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  try {
    // Si c'est un objet Date, on vérifie juste qu'il est valide
    if (date instanceof Date) {
      return !isNaN(date.getTime());
    }

    // Pour les strings, on extrait les composants de la date
    const dateStr = String(date).trim();

    // Format attendu: YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss.SSSZ
    let year: number, month: number, day: number;

    // Gérer le format ISO avec timestamp
    if (dateStr.includes('T')) {
      const datePart = dateStr.split('T')[0];
      const parts = datePart.split('-');
      if (parts.length !== 3) return false;

      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    }
    // Gérer le format YYYY-MM-DD
    else if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length !== 3) return false;

      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    }
    // Gérer le format DD/MM/YYYY
    else if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length !== 3) return false;

      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    }
    // Format non reconnu
    else {
      return false;
    }

    // Vérifier les composants de base
    if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    // Créer la date et vérifier qu'elle n'a pas été normalisée
    const d = new Date(year, month - 1, day);

    // Vérifier que la date créée correspond exactement à l'input
    return (
      d.getFullYear() === year &&
      d.getMonth() === month - 1 &&
      d.getDate() === day
    );
  } catch {
    return false;
  }
};

/**
 * Ajouter des jours à une date
 *
 * @param date - Date de départ
 * @param days - Nombre de jours à ajouter
 * @returns Nouvelle date avec les jours ajoutés
 *
 * @example
 * addDays("2025-10-21", 7) // "2025-10-28"
 * addDays(new Date("2025-10-21"), 30) // Date du 20 novembre 2025
 */
export const addDays = (date: Date | string, days: number): Date => {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Calculer la différence en jours entre deux dates
 *
 * @param date1 - Première date
 * @param date2 - Deuxième date
 * @returns Nombre de jours entre les deux dates (peut être négatif)
 *
 * @example
 * diffInDays("2025-10-21", "2025-10-28") // 7
 * diffInDays("2025-10-28", "2025-10-21") // -7
 */
export const diffInDays = (date1: Date | string, date2: Date | string): number => {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;

  const diffTime = d2.getTime() - d1.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Vérifier si une date est dans le passé
 *
 * @param date - Date à vérifier
 * @returns true si la date est dans le passé, false sinon
 *
 * @example
 * isPast("2020-10-21") // true
 * isPast("2030-10-21") // false
 */
export const isPast = (date: Date | string): boolean => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.getTime() < Date.now();
};

/**
 * Vérifier si une date est dans le futur
 *
 * @param date - Date à vérifier
 * @returns true si la date est dans le futur, false sinon
 *
 * @example
 * isFuture("2020-10-21") // false
 * isFuture("2030-10-21") // true
 */
export const isFuture = (date: Date | string): boolean => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.getTime() > Date.now();
};

/**
 * Obtenir la date du jour au format "YYYY-MM-DD"
 *
 * @returns String au format "YYYY-MM-DD"
 *
 * @example
 * getToday() // "2025-10-22"
 */
export const getToday = (): string => {
  return toInputFormat(new Date());
};

/**
 * Obtenir la date du jour + X jours au format "YYYY-MM-DD"
 *
 * @param days - Nombre de jours à ajouter
 * @returns String au format "YYYY-MM-DD"
 *
 * @example
 * getTodayPlusDays(7) // "2025-10-29" (si aujourd'hui = 2025-10-22)
 * getTodayPlusDays(30) // "2025-11-21"
 */
export const getTodayPlusDays = (days: number): string => {
  return toInputFormat(addDays(new Date(), days));
};

// Export d'un objet pour usage avec namespace
export const DateUtils = {
  toInputFormat,
  toISO,
  formatDate,
  formatDateLong,
  toFrench,
  toFrenchLong,
  toFacturX,
  isValidDate,
  isStrictValidDate,
  addDays,
  diffInDays,
  isPast,
  isFuture,
  getToday,
  getTodayPlusDays,
};

export default DateUtils;
